import React, { forwardRef, useCallback, useMemo } from "react";
import { StriimModal } from "@striim/striim-ui-v2";
import { useParams } from "react-router";
import SidebarWrapper, { SidebarWrapperRef } from "./sidebar-wrapper";
import SentinelSidebarWrapper, { SentinelSidebarWrapperRef } from "./sentinel-sidebar-wrapper";
import FormTitle from "./form-title";
import { FormTypes } from "../../flow/flow.constants";
import { deleteNode } from "../../helpers/flow.utils";
import { useAppDispatch, useAppSelector } from "src/stores/redux/hooks";
import {
    getIsExceptionsSidebarOpen,
    getFormType,
    getCurrentFlowId,
    getIsRightSidebarOpen,
    getSelectedNode,
    getAppFQN
} from "../../helpers/selectors";
import {
    closeRightSidebarAction,
    deleteNodeAction,
    setSelectedNodeAction,
    setFormTypeAction
} from "src/stores/redux/features/flowdesigner/flowdesigner.slice";
import { useFetchAndPopulateNodes } from "../../helpers/flow.hooks";
import metaObjectConverter from "core/services/metaStoreService/metaobject-converter";
import metaStoreService from "core/services/metaStoreService/meta-store-service";
import { navigateToNode } from "../../helpers/navigation.utils";
import { isTempNode } from "../../helpers/node.utils";
import api from "core/api/api";
import growl from "app/components/common/growl";
import localErrorsStorage from "app/components/flow/designer/appErrors/localErrorsStorage";
import { previewData } from "../../stream-preview/preview-data.utils";
import { FlowUrlProps } from "../../flowdesigner";

type Props = {
    promptIfUnsavedChanged: () => Promise<boolean>;
    appModel: any;
    appStatus: string | null;
};

const NodeForm = forwardRef<SidebarWrapperRef | SentinelSidebarWrapperRef, Props>((props, ref) => {
    const { promptIfUnsavedChanged, appModel, appStatus } = props;

    const handleClearAndReload = useCallback(() => {
        localErrorsStorage.clear(appModel);
    }, [appModel]);

    const isExceptionsSidebarOpen = useAppSelector(getIsExceptionsSidebarOpen);
    const formType = useAppSelector(getFormType);
    const currentFlowId = useAppSelector(getCurrentFlowId);
    const isRightSidebarOpen = useAppSelector(getIsRightSidebarOpen);
    const selectedNode = useAppSelector(getSelectedNode);
    const appFQN = useAppSelector(getAppFQN);
    const fetchAndPopulateNodes = useFetchAndPopulateNodes();
    const dispatch = useAppDispatch();
    const handlePreviewData = useCallback((model: any) => previewData(dispatch, model), [dispatch]);
    const { appName, flowName } = useParams<FlowUrlProps>();
    const toggleFormType = useCallback(() => {
        dispatch(setFormTypeAction(formType === FormTypes.SIDEBAR ? FormTypes.MODAL : FormTypes.SIDEBAR));
    }, [dispatch, formType]);

    // Determine if the selected node is a Sentinel node
    const isSentinelNode = useMemo(() => {
        return selectedNode?.data?.metaObject?.type === metaStoreService.entities.SENTINEL;
    }, [selectedNode]);

    const handleSidebarSave = useCallback(
        async (saveEventData?: any) => {
            // Compile app
            try {
                await api.compile(appModel.name);
            } catch (error) {
                growl.error(String(error), "Error Saving your component.", appModel.name, appModel.name);
                await fetchAndPopulateNodes();
                return;
            }
            // Clear errors if compilation is successful
            localErrorsStorage.clear(appModel);
            if (!saveEventData?.id || !selectedNode) return;

            const metaObject = await metaStoreService.findById(saveEventData.id);
            const nodeName = metaObjectConverter.getName(saveEventData.id);
            const metaObjectJson = metaObject.toJSON();

            const shouldCloseSidebar =
                metaObjectJson?.uiconfig?.subType === "Foreseer" || metaObjectJson?.uiconfig?.subType === "Euclid";

            if (shouldCloseSidebar) {
                dispatch(closeRightSidebarAction({ appName, flowName }));
                await fetchAndPopulateNodes({ skipReCenter: true });
                return;
            }

            const updatedNode = {
                ...selectedNode,
                id: saveEventData.id,
                data: {
                    ...selectedNode.data,
                    name: nodeName,
                    metaObjectId: saveEventData.id,
                    metaObject: metaObjectJson
                }
            };
            dispatch(setSelectedNodeAction(updatedNode));

            // Navigate to the node URL after save
            if (appName && nodeName) {
                navigateToNode(flowName, appName, nodeName);
            }
            await fetchAndPopulateNodes({ skipReCenter: true });

            // If this Sentinel was created from Discover PII, rewire original downstream targets
            // to consume the Sentinel's outputStream instead of the previous stream directly
            const targetsToRewire = selectedNode.data?.targetsToBeConnectedWithSentinel;
            if (targetsToRewire?.length && saveEventData?.outputStream) {
                await Promise.all(
                    targetsToRewire.map(async (targetId: string) => {
                        const targetModel = await metaStoreService.findById(targetId);
                        targetModel.set("inputStream", saveEventData.outputStream);
                        return targetModel.save({ flow: appModel.name });
                    })
                );
                await fetchAndPopulateNodes({ skipReCenter: true });
            }

            // Re-dispatch selectedNode after nodes are repopulated so the centering
            // effect in useNodeSelection re-runs with the correct ReactFlow node position
            dispatch(setSelectedNodeAction({ ...updatedNode }));
        },
        [fetchAndPopulateNodes, selectedNode, dispatch, appName, flowName, appModel, appFQN]
    );

    const handleSidebarCancel = useCallback(async () => {
        const shouldProceed = await promptIfUnsavedChanged();
        if (shouldProceed) {
            if (isTempNode(selectedNode.data)) {
                dispatch(deleteNodeAction(selectedNode.id));
            }

            dispatch(closeRightSidebarAction({ appName, flowName }));
        }
    }, [promptIfUnsavedChanged, dispatch, appName, flowName]);

    const handleDelete = useCallback(async () => {
        if (!selectedNode) return;

        try {
            const result = await deleteNode(selectedNode, appModel, fetchAndPopulateNodes);

            if (result) {
                dispatch(deleteNodeAction(result.nodeId));
                dispatch(closeRightSidebarAction({ appName, flowName }));
            }
        } catch (error) {
            console.error("Delete node error:", error);
        }
    }, [selectedNode, appModel, fetchAndPopulateNodes, dispatch, appName, flowName]);

    if (isRightSidebarOpen && selectedNode && !isExceptionsSidebarOpen)
        return (
            <>
                {formType === FormTypes.MODAL ? (
                    <StriimModal
                        isVisible={isRightSidebarOpen}
                        onCancel={handleSidebarCancel}
                        cancelContent="Close"
                        confirmContent="Save"
                        onConfirm={handleSidebarSave}
                        titleContent={<FormTitle node={selectedNode} type={formType} toggleFormType={toggleFormType} />}
                        size="medium"
                        dialogProps={
                            {
                                sx: {
                                    ".MuiDialog-paper": { width: "800px", height: "75vh" },
                                    ".MuiDialogTitle-root": { padding: 0, paddingTop: "4px" },
                                    ".MuiDialogContent-root": { padding: 0 },
                                    ".MuiDialogActions-root": { padding: 0 }
                                }
                            } as any
                        }
                        footerContent={<></>}
                    >
                        {isSentinelNode ? (
                            <SentinelSidebarWrapper
                                ref={ref as React.ForwardedRef<SentinelSidebarWrapperRef>}
                                selectedNode={selectedNode}
                                flowId={currentFlowId || ""}
                                app={appModel}
                                appStatus={appStatus}
                                onSidebarSave={handleSidebarSave}
                                onSidebarCancel={handleSidebarCancel}
                                onDelete={handleDelete}
                                formType={formType}
                                toggleFormType={toggleFormType}
                            />
                        ) : (
                            <SidebarWrapper
                                ref={ref as React.ForwardedRef<SidebarWrapperRef>}
                                selectedNode={selectedNode}
                                flowId={currentFlowId || ""}
                                app={appModel}
                                appStatus={appStatus}
                                onSidebarSave={handleSidebarSave}
                                onSidebarCancel={handleSidebarCancel}
                                onDelete={handleDelete}
                                onPreviewData={handlePreviewData}
                                onClearAndReload={handleClearAndReload}
                                formType={formType}
                                toggleFormType={toggleFormType}
                            />
                        )}
                    </StriimModal>
                ) : isSentinelNode ? (
                    <SentinelSidebarWrapper
                        ref={ref as React.ForwardedRef<SentinelSidebarWrapperRef>}
                        selectedNode={selectedNode}
                        flowId={currentFlowId || ""}
                        app={appModel}
                        appStatus={appStatus}
                        onSidebarSave={handleSidebarSave}
                        onSidebarCancel={handleSidebarCancel}
                        onDelete={handleDelete}
                        formType={formType}
                        toggleFormType={toggleFormType}
                    />
                ) : (
                    <SidebarWrapper
                        ref={ref as React.ForwardedRef<SidebarWrapperRef>}
                        selectedNode={selectedNode}
                        flowId={currentFlowId || ""}
                        app={appModel}
                        appStatus={appStatus}
                        onSidebarSave={handleSidebarSave}
                        onSidebarCancel={handleSidebarCancel}
                        onDelete={handleDelete}
                        onPreviewData={handlePreviewData}
                        onClearAndReload={handleClearAndReload}
                        formType={formType}
                        toggleFormType={toggleFormType}
                    />
                )}
            </>
        );
});

export default NodeForm;
